// src/utils/logoVorbereiten.js
//
// Sponsorlogo vor dem Upload im Browser aufbereiten.
//
// Das Backend nimmt nur PNG/JPEG bis 160 KB (services/sponsorLogos.js) – die
// Grenze ergibt sich aus dem JSON-Limit der API. Logos kommen aber als
// Handyfoto, als 5-MB-Druckvorlage oder als WebP/SVG von der Website. Darum hier:
//
//   1. Bild laden (alles, was der Browser anzeigen kann).
//   2. Leere Raender abschneiden (transparent oder fast weiss). Viele Logos
//      bringen reichlich Weissraum mit und wuerden auf dem Poster sonst winzig.
//   3. Auf hoechstens MAX_BREITE × MAX_HOEHE verkleinern – mehr braucht selbst
//      das A3-Poster nicht (das Logo ist dort gut 10 cm breit).
//   4. Als PNG ausgeben (Transparenz bleibt); ist das zu gross, kleiner rechnen,
//      zuletzt als JPEG auf weissem Grund.

const MAX_BREITE = 1200;
const MAX_HOEHE = 600;
// Etwas unter der Backend-Grenze, damit Rundung nie zur Ablehnung fuehrt.
const MAX_BYTES = 150 * 1024;
const STUFEN = [1, 0.75, 0.5, 0.35];

function bildLaden(datei) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(datei);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Die Datei konnte nicht als Bild gelesen werden."));
    };
    img.src = url;
  });
}

/** Rechteck des tatsaechlichen Motivs: ohne transparente/weisse Randzeilen. */
function motivRahmen(ctx, breite, hoehe) {
  const { data } = ctx.getImageData(0, 0, breite, hoehe);
  const leer = (x, y) => {
    const i = (y * breite + x) * 4;
    return data[i + 3] < 12 || (data[i] > 245 && data[i + 1] > 245 && data[i + 2] > 245);
  };
  const zeileLeer = (y) => {
    for (let x = 0; x < breite; x++) if (!leer(x, y)) return false;
    return true;
  };
  const spalteLeer = (x, y0, y1) => {
    for (let y = y0; y <= y1; y++) if (!leer(x, y)) return false;
    return true;
  };

  let oben = 0;
  while (oben < hoehe - 1 && zeileLeer(oben)) oben++;
  // Ganz leeres (oder ganz weisses) Bild: nichts abschneiden.
  if (oben === hoehe - 1 && zeileLeer(oben)) return { x: 0, y: 0, breite, hoehe };
  let unten = hoehe - 1;
  while (unten > oben && zeileLeer(unten)) unten--;
  let links = 0;
  while (links < breite - 1 && spalteLeer(links, oben, unten)) links++;
  let rechts = breite - 1;
  while (rechts > links && spalteLeer(rechts, oben, unten)) rechts--;

  return { x: links, y: oben, breite: rechts - links + 1, hoehe: unten - oben + 1 };
}

const bytesVon = (dataUrl) => Math.floor(((dataUrl.length - dataUrl.indexOf(",") - 1) * 3) / 4);

/**
 * @param {File} datei
 * @returns {Promise<{ dataUrl: string, breite: number, hoehe: number }>}
 */
export async function logoVorbereiten(datei) {
  if (!datei || !String(datei.type).startsWith("image/")) {
    throw new Error("Bitte eine Bilddatei wählen (PNG, JPG, WebP oder SVG).");
  }
  const img = await bildLaden(datei);
  const quelleB = img.naturalWidth || img.width;
  const quelleH = img.naturalHeight || img.height;
  if (!quelleB || !quelleH) throw new Error("Das Bild hat keine lesbaren Maße.");

  // Arbeitskopie in hoechstens doppelter Zielgroesse – ein 6000-px-Foto Pixel
  // fuer Pixel nach Raendern abzusuchen, wuerde das Handy spuerbar bremsen.
  const vor = Math.min(1, (2 * MAX_BREITE) / quelleB, (2 * MAX_HOEHE) / quelleH);
  const arbeit = document.createElement("canvas");
  arbeit.width = Math.max(1, Math.round(quelleB * vor));
  arbeit.height = Math.max(1, Math.round(quelleH * vor));
  const actx = arbeit.getContext("2d", { willReadFrequently: true });
  actx.drawImage(img, 0, 0, arbeit.width, arbeit.height);
  const a = motivRahmen(actx, arbeit.width, arbeit.height);

  // Zugeschnitten wird aus dem ORIGINAL: ein kleines Logo auf grosser Flaeche
  // verloere sonst schon durch die Arbeitskopie Aufloesung.
  const r = { x: a.x / vor, y: a.y / vor, breite: a.breite / vor, hoehe: a.hoehe / vor };
  // Pixelbilder nur verkleinern; ein SVG ist Vektor und darf auf die volle
  // Zielgroesse wachsen (Website-Logos haben oft nur 150 px Nenngroesse).
  const vektor = datei.type === "image/svg+xml";
  const basis = Math.min(vektor ? Infinity : 1, MAX_BREITE / r.breite, MAX_HOEHE / r.hoehe);
  const zeichnen = (faktor, weiss) => {
    const c = document.createElement("canvas");
    c.width = Math.max(1, Math.round(r.breite * basis * faktor));
    c.height = Math.max(1, Math.round(r.hoehe * basis * faktor));
    const ctx = c.getContext("2d");
    if (weiss) {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, c.width, c.height);
    }
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, r.x, r.y, r.breite, r.hoehe, 0, 0, c.width, c.height);
    return c;
  };

  // Erst die Groesse halten, dann das Format wechseln: ein JPEG in voller
  // Aufloesung druckt besser als ein briefmarkengrosses PNG. Die Transparenz
  // geht dabei verloren, schadet aber nicht – Poster und Zettel setzen das
  // Logo ohnehin auf Weiss.
  for (const faktor of STUFEN) {
    for (const format of ["image/png", "image/jpeg"]) {
      const c = zeichnen(faktor, format === "image/jpeg");
      const dataUrl = c.toDataURL(format, 0.88);
      if (bytesVon(dataUrl) <= MAX_BYTES) return { dataUrl, breite: c.width, hoehe: c.height };
    }
  }
  throw new Error("Das Logo ist auch verkleinert noch zu groß.");
}
